/**
 * Trips-table CSV export (TW back-office "All trips" → Export).
 *
 * The back-office trips table exports every row matching the current filters to a CSV the operator
 * downloads. Like the manifest export ([[manifest-export]]) the byte assembly is a **pure,
 * clock-free fold**: the caller resolves the locale-specific bits (club name, localized status,
 * number/date formatting) into a flat {@link TripExportRow}, and this leaf fixes the column order +
 * RFC-4180 escaping. That keeps it unit-testable without a browser, and guarantees the header and
 * body always line up.
 *
 * Finance columns (margin etc.) are back-office-only data — the export button only exists on the
 * back-office table, so this builder is never reached for a club user.
 */

/** The ordered export columns; keys map to localized headers and fix the cell order in every row. */
export const TRIP_EXPORT_COLUMNS = [
  "reference",
  "club",
  "trip",
  "competition",
  "destination",
  "startDate",
  "endDate",
  "pax",
  "status",
  "sellTotal",
  "netCost",
  "margin",
  "marginPct",
] as const;
export type TripExportColumn = (typeof TRIP_EXPORT_COLUMNS)[number];

/**
 * One trip flattened to its export cells — all strings, already localized/formatted by the caller.
 * Empty string for a value the trip doesn't have (e.g. an unpriced trip's finance columns).
 */
export type TripExportRow = Record<TripExportColumn, string>;

/** Resolved, localized header strings (one per {@link TRIP_EXPORT_COLUMNS} key). */
export interface TripExportLabels {
  columns: Record<TripExportColumn, string>;
}

/** Map one row to its ordered cells (column order = {@link TRIP_EXPORT_COLUMNS}). Pure. */
export function tripExportRow(row: TripExportRow): string[] {
  return TRIP_EXPORT_COLUMNS.map((c) => row[c]);
}

/** Escape one CSV cell per RFC-4180: quote it when it contains a comma, quote or newline. */
function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Build the full trips CSV (a header row followed by one row per trip), CRLF-terminated per
 * RFC-4180. Pure + deterministic — the caller prepends a UTF-8 BOM when turning this into a Blob so
 * Excel renders accented club/destination names correctly (a byte concern kept out of here).
 */
export function buildTripsCsv(rows: TripExportRow[], labels: TripExportLabels): string {
  const header = TRIP_EXPORT_COLUMNS.map((c) => labels.columns[c]);
  const lines = [header, ...rows.map(tripExportRow)];
  return lines.map((cells) => cells.map(csvCell).join(",")).join("\r\n");
}
